// External Libraries
import * as React from "react";
import { observer } from "mobx-react";

// Types
import { Hotel } from "typings/microserviceModels/hotels-flex-module";

// UITK
import { UitkCardLink, UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { classNames } from "uitk-react-utils";

// Components
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useHotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import { SummarizedContent } from "components/flexComponents/Hotels/components/SummarizedHotelCard/SummarizedContent";

// Utils
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";

interface SummarizedHotelCardProps {
  hotel: Hotel;
  index: number;
  isMobile: boolean;
  className?: string;
  imageAspectRatio?: UitkFigureAspectRatioType;
}

export const SummarizedHotelCardComponent: React.FC<SummarizedHotelCardProps> = ({
  hotel,
  index,
  isMobile,
  className,
  imageAspectRatio,
}) => {
  const { templateComponent, context } = useHotelsContext();
  const hotelImageUrl = getOptimizedImageUrl(hotel, context);
  const { hotelName, hotelInfositeUrl } = hotel;
  const moduleName = templateComponent.type as string;

  return (
    <UitkCard className={classNames("SummarizedHotelCard", className)}>
      <UitkLayoutGrid columns={6} data-testid="summarized-hotelcard">
        <>
          <UitkLayoutGridItem colSpan={2}>
            <UitkFigure
              ratio={imageAspectRatio || (isMobile ? UitkFigureAspectRatioType.R1_1 : UitkFigureAspectRatioType.R4_3)}
            >
              <LazyLoadedImage src={hotelImageUrl} alt={hotelName} />
            </UitkFigure>
          </UitkLayoutGridItem>
          <UitkLayoutGridItem colSpan={4}>
            <UitkCardContentSection>
              <SummarizedContent hotel={hotel} isMobile={isMobile} />
              <UitkCardLink>
                <TrackedLink
                  data-testid={`hotelcard-link-${index}`}
                  className="uitk-card-link"
                  moduleName={moduleName}
                  rfrr="card"
                  href={hotelInfositeUrl}
                >
                  {hotelName}
                </TrackedLink>
              </UitkCardLink>
            </UitkCardContentSection>
          </UitkLayoutGridItem>
        </>
      </UitkLayoutGrid>
    </UitkCard>
  );
};

export const SummarizedHotelCard = observer(SummarizedHotelCardComponent);

SummarizedHotelCard.displayName = "SummarizedHotelCard";
